import { tokens } from '@neo4j-ndl/base';
import { Drawer, Tag, Typography, useMediaQuery } from '@neo4j-ndl/react';
import { ExclamationTriangleIconOutline } from '@neo4j-ndl/react/icons';
import { FleetManagementTypes, consoleApi, useActiveProject } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { useMemo } from 'react';

import { DeploymentStatus } from '../shared/components/deployment-status';
import { PropertyDisplay } from '../shared/components/property-display';

interface LicenseDetailsProps {
  license?: FleetManagementTypes.License;
  serverAddress?: string;
}

const LicenseDetails = ({ license, serverAddress }: LicenseDetailsProps) => {
  if (isNullish(license)) {
    return (
      <Tag type="destructive" size="medium" className="flex w-fit items-center gap-1">
        <ExclamationTriangleIconOutline className="h-4 w-4" />
        <Typography variant="body-medium">Missing license information</Typography>
      </Tag>
    );
  }

  let typeText: string;
  switch (license.type) {
    case FleetManagementTypes.Api.LICENSE_TYPE.UNSUPPORTED:
      typeText = 'License information for the current version is not supported';
      break;
    case FleetManagementTypes.Api.LICENSE_TYPE.INCONSISTENT:
      typeText = 'License is inconsistent between servers';
      break;
    default:
      typeText = `${license.type}`;
      break;
  }

  const warningState =
    license.state === FleetManagementTypes.Api.LICENSE_STATE.INCONSISTENT ||
    license.state === FleetManagementTypes.Api.LICENSE_STATE.UNKNOWN ||
    license.state === FleetManagementTypes.Api.LICENSE_STATE.EXPIRED ||
    license.state === FleetManagementTypes.Api.LICENSE_STATE.NOT_ACCEPTED ||
    license.state === FleetManagementTypes.Api.LICENSE_STATE.INVALID;

  return (
    <>
      {isNotNullish(serverAddress) && <PropertyDisplay label="Server" text={serverAddress} />}
      <PropertyDisplay label="Type" text={typeText} />
      {warningState && (
        <Tag type="destructive" size="medium" className="flex w-fit items-center gap-1">
          <ExclamationTriangleIconOutline className="h-4 w-4" />
          <Typography variant="body-medium">{license.state}</Typography>
        </Tag>
      )}
      {!warningState && <PropertyDisplay label="State" text={license.state} />}
      {license.daysLeftOnTrial && (
        <>
          {!license.totalTrialDays ? (
            <PropertyDisplay label="License valid for" text={`${license.daysLeftOnTrial} days`} />
          ) : (
            <PropertyDisplay
              label="Trial"
              text={`${license.daysLeftOnTrial} days left of ${license.totalTrialDays} days`}
            />
          )}
        </>
      )}
    </>
  );
};

interface LicenseProps {
  license?: FleetManagementTypes.License;
  productName: string;
  servers?: FleetManagementTypes.Server[];
}

const License = ({ license, productName, servers }: LicenseProps) => {
  const isInconsistent = license?.state === FleetManagementTypes.Api.LICENSE_STATE.INCONSISTENT;

  return (
    <div className="border-neutral-border-weak mb-3 gap-x-4 gap-y-1 rounded-[8px] border px-3 py-3">
      <h6 className="font-semibold">{productName}</h6>
      <LicenseDetails license={license} />
      {isInconsistent && servers && (
        <>
          <div className="mt-2">
            <h6 className="mb-1 text-sm font-medium">Server Licenses</h6>
            {servers.map((server) => {
              let serverLicense;
              if (productName === 'Neo4j') {
                serverLicense = server.license;
              } else if (productName === 'Graph Data Science') {
                serverLicense = server.gdsLicense;
              } else {
                serverLicense = server.bloomLicense;
              }

              return (
                <div key={server.serverId} className="border-neutral-border-weak mb-2 border-l-2 pl-2">
                  <LicenseDetails license={serverLicense} serverAddress={server.address} />
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

interface DeploymentInspectDrawerProps {
  dbms: FleetManagementTypes.DbmsDetails;
  onClose: () => void;
}

export const DeploymentInspectDrawer = ({ dbms, onClose }: DeploymentInspectDrawerProps) => {
  const { breakpoints } = tokens;
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.sm})`);
  const activeProject = useActiveProject();
  const getProjectMembersRes = consoleApi.useListProjectMembersQuery(activeProject.id);
  const creator = getProjectMembersRes.data?.find((member) => member.userId === dbms.tokenCreatedBy)?.email;

  const defaultWidth = useMemo(() => {
    if (isMobile) {
      return window.innerWidth;
    }
    return 720;
  }, [isMobile]);

  return (
    <Drawer
      isExpanded
      onExpandedChange={onClose}
      position="right"
      className="!border-l"
      isResizeable
      resizeableProps={{
        defaultSize: { height: '100%', width: defaultWidth },
        minWidth: defaultWidth,
      }}
    >
      <Drawer.Header className="flex items-center gap-4">
        <h5 className="ml-1">{dbms.name}</h5>
        <DeploymentStatus status={dbms.status} />
      </Drawer.Header>
      <Drawer.Body className="flex flex-col">
        <div className="mb-6 flex flex-col gap-2">
          <PropertyDisplay label="Version" text={dbms.version} />
          <PropertyDisplay label="Edition" text={dbms.edition} />
          <PropertyDisplay label="Packaging" text={dbms.packaging} />
          <PropertyDisplay label="Servers" text={dbms.servers.map((server) => server.address).join(', ')} />
          <PropertyDisplay label="Registered by" text={creator ?? 'unknown user'} />
        </div>
        <div className="mb-6">
          <h6 className="mb-2 font-semibold">Licenses</h6>
          <License license={dbms.license} productName="Neo4j" servers={dbms.servers} />
          {dbms.gdsLicense && (
            <License license={dbms.gdsLicense} productName="Graph Data Science" servers={dbms.servers} />
          )}
          {dbms.bloomLicense && <License license={dbms.bloomLicense} productName="Bloom" servers={dbms.servers} />}
        </div>
      </Drawer.Body>
    </Drawer>
  );
};

interface LicenseIssueBadgeProps {
  onClick: () => void;
}

export const LicenseIssueBadge = ({ onClick }: LicenseIssueBadgeProps) => {
  return (
    <div onClick={onClick} className="cursor-pointer">
      <Tag type="destructive" size="medium" className="flex w-fit items-center gap-1">
        <ExclamationTriangleIconOutline className="h-4 w-4" />
        <Typography variant="body-medium">License issue detected</Typography>
      </Tag>
    </div>
  );
};
